
import { handleNewDay } from "@/lib/wordLibrary";
import { loadGameState, clearGameState, saveGameState } from "../GamePersistenceV2";
import { GameStatus } from "@/lib/types/GameTypes";
import { GameInitializationResult } from "../types/InitializationTypes";
import { formatDate, getEffectiveDate, normalizeDate } from "@/lib/dateUtils";
import { getDebugParams, isDebugDateMode } from "@/lib/debugUtils";
import { getCurrentGameStatus } from "./GameStatusManager";
import { toast } from "sonner";

import { getSequenceIdFromUrl } from "@/lib/sequenceUtils";
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import { CompletionStateManager } from "./CompletionStateManager";
import { sharedSequenceStateManager } from "./SharedSequenceStateManager";
import { OverlayStateCoordinator } from "./OverlayStateCoordinator";

export const initializeGameState = async (date: Date, wordSequence: string[]): Promise<GameInitializationResult> => {
  try {
    // Check for shared sequence (active or just present in URL)
    const sequenceIdFromUrl = getSequenceIdFromUrl();
    const isSharedSequenceActive = sharedSequenceStateManager.isSharedSequenceActive();
    const isSharedSequence = isSharedSequenceActive || !!sequenceIdFromUrl;
    const sequenceId = isSharedSequenceActive ? sharedSequenceStateManager.getSequenceId() : sequenceIdFromUrl;
    
    // Check for various flags that might affect initialization
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    const globalPlayAgain = localStorage.getItem('babblegram_play_again') === 'true';
    const practicePlayAgain = localStorage.getItem('babblegram_practice_mode_play_again') === 'true';
    const isPlayAgain = globalPlayAgain || (currentMode === 'practice' && practicePlayAgain);
    const isModeSwitch = localStorage.getItem('babblegram_mode_switch_in_progress') === 'true';
    const forceModeChange = localStorage.getItem('babblegram_force_mode_change') === 'true';
    const previousMode = localStorage.getItem('babblegram_previous_mode');
    const nextMode = localStorage.getItem('babblegram_next_mode');
    const fromWelcomePage = localStorage.getItem('from_welcome_page') === 'true';
    
    console.log('Game initialization flags:', {
      isPlayAgain,
      isModeSwitch,
      forceModeChange,
      fromWelcomePage,
      currentMode,
      previousMode,
      nextMode,
      isSharedSequence,
      sequenceId,
      date: date.toISOString(),
      timestamp: new Date().toISOString()
    });
    
    // Clear flags that should only apply once
    if (isPlayAgain) {
      console.log('Play Again flag detected in GameInitializationManager', {
        mode: currentMode,
        timestamp: new Date().toISOString()
      });
      localStorage.removeItem('babblegram_play_again');
      if (currentMode === 'practice') {
        localStorage.removeItem('babblegram_practice_mode_play_again');
      }
    }
    
    if (isModeSwitch) {
      console.log('Mode switch flag detected in GameInitializationManager', {
        mode: currentMode,
        timestamp: new Date().toISOString()
      });
      localStorage.removeItem('babblegram_mode_switch_in_progress');
    }
    
    if (forceModeChange) {
      console.log('Force change flag detected in GameInitializationManager', {
        mode: currentMode,
        timestamp: new Date().toISOString()
      });
      localStorage.removeItem('babblegram_force_mode_change');
      
      // Clear any sequence caches to ensure we get the right word sequence
      localStorage.removeItem('babblegram_current_sequence');
      localStorage.removeItem('babblegram_level_state');
      
      if (currentMode === 'practice' && previousMode !== 'daily') {
        localStorage.removeItem('babblegram_practice_sequence');
      }
    }
    
    if (fromWelcomePage) {
      console.log('From welcome page flag detected in GameInitializationManager', {
        mode: currentMode,
        timestamp: new Date().toISOString()
      });
      localStorage.removeItem('from_welcome_page');
      
      // Mark that instructions have been shown to prevent overlay
      const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
      localStorage.setItem(`${prefix}instructions_shown`, 'true');
      
      // Mark that in-progress overlay has been shown
      localStorage.setItem(`${prefix}in_progress_overlay_shown`, 'true');
    }
    
    // Ensure date is normalized to midnight for consistency
    const normalizedDate = normalizeDate(date);
    
    // Check for new day - this is the ONLY place that should call handleNewDay()
    const { isNewDay, effectiveDate } = forceModeChange ? 
      { isNewDay: false, effectiveDate: normalizedDate } : 
      await handleNewDay().catch(error => {
        console.error('Error checking for new day:', error);
        return { isNewDay: false, effectiveDate: normalizedDate };
      });
    
    // Date is handled by the word sequence provider
    
    console.log('Game mode for initialization:', {
      mode: currentMode,
      isNewDay,
      isPlayAgain,
      isModeSwitch,
      forceModeChange,
      effectiveDate: effectiveDate.toISOString(),
      timestamp: new Date().toISOString()
    });
    
    // Use the provided word sequence
    const sequence = wordSequence;
    if (!sequence || !Array.isArray(sequence) || sequence.length === 0) {
      console.error('Invalid word sequence:', sequence);
      throw new Error('Invalid word sequence');
    }
    
    // Load saved state
    const savedState = await loadGameState();
    
    // Check for shared sequence conflict in practice mode
    if ((isSharedSequence || sequenceIdFromUrl) && currentMode === 'practice') {
      const isCompleted = CompletionStateManager.hasCompletedGame('practice');
      
      // Determine if this shared sequence has already been handled
      const effectiveSequenceId = sequenceId || sequenceIdFromUrl || '';
      const handledKey = `babblegram_shared_sequence_handled_${effectiveSequenceId}`;
      const alreadyHandled = effectiveSequenceId ? localStorage.getItem(handledKey) === 'true' : false;
      
      // Quick state checks
      const practiceInProgress = directCookieAccess.hasInProgressGame('practice');
      const practiceCompleted = directCookieAccess.hasCompletedGame('practice');
      
      // Determine if there's meaningful progress to protect
      let hasProgress = false;
      if (savedState) {
        const levelProgress = (savedState.currentLevel || 1) > 1;
        const guessProgress = (savedState.guesses?.length || 0) > 0 ||
          (savedState.guessHistory?.some(h => (h.guesses?.length || 0) > 0) ?? false);
        hasProgress = levelProgress || guessProgress;
      } else {
        // Fallback to cookie-based detection if saved state is unavailable
        const rawLevel = modeAwareStorage.getCookieForMode('current_level', 'practice');
        const rawHistory = modeAwareStorage.getCookieForMode('guess_history', 'practice');
        const levelNum = rawLevel ? parseInt(rawLevel) : 1;
        const historyHasGuesses = (() => {
          try {
            const h = rawHistory ? JSON.parse(rawHistory) : [];
            return Array.isArray(h) && h.some((x: any) => Array.isArray(x?.guesses) && x.guesses.length > 0);
          } catch {
            return false;
          }
        })();
        hasProgress = levelNum > 1 || historyHasGuesses;
      }
      
      console.log('🔍 Shared sequence conflict check (enhanced):', {
        isSharedSequence,
        currentMode,
        hasSavedState: !!savedState,
        isCompleted,
        practiceInProgress,
        practiceCompleted,
        hasProgress,
        sequenceId: effectiveSequenceId,
        alreadyHandled,
        timestamp: new Date().toISOString()
      });
      
      if (alreadyHandled) {
        console.log('🔍 Shared sequence already handled, proceeding normally');
        // Don't show overlay, let normal game flow continue
      } else if (isCompleted) {
        // If practice game is completed, clear it and start fresh with shared sequence
        console.log('🔍 Practice game completed, starting fresh with shared sequence');
        await clearGameState();
        localStorage.setItem('babblegram_practice_mode_use_shared_sequence', 'true');
        localStorage.setItem(handledKey, 'true');
      } else if (practiceInProgress && !practiceCompleted && hasProgress) {
        // If practice game is in progress with meaningful progress, show conflict overlay
        console.log('🔍 Practice game in progress, showing shared sequence conflict overlay');
        return {
          currentLevel: 1,
          currentWord: sequence[0],
          wordSequence: sequence,
          showInstructions: false,
          showGameCompletion: false,
          showInProgressOverlay: false,
          showSharedSequenceConflict: true,
          sequenceId: effectiveSequenceId,
          redirectToWelcome: false,
          attempts: 0,
          gameStatus: 'playing' as GameStatus
        };
      }
    }
    
    console.log('Game initialization state:', {
      isNewDay,
      isPlayAgain,
      isModeSwitch,
      forceModeChange,
      hasSavedState: !!savedState,
      mode: currentMode,
      firstWord: sequence[0],
      isSharedSequence,
      date: date.toISOString(),
      timestamp: new Date().toISOString()
    });

    // CRITICAL FIX: Use OverlayStateCoordinator for atomic overlay decisions
    // This eliminates race conditions by capturing all state once
    const snapshot = OverlayStateCoordinator.captureStateSnapshot(currentMode as 'daily' | 'practice');
    const overlayDecision = OverlayStateCoordinator.determineOverlayState(snapshot);
    
    console.log('[GameInit] Overlay decision made:', {
      showInProgress: overlayDecision.showInProgress,
      showGameCompletion: overlayDecision.showGameCompletion,
      shouldRedirectToWelcome: overlayDecision.shouldRedirectToWelcome,
      timestamp: new Date().toISOString()
    });
    
    // If redirect is needed, return immediately without further processing
    if (overlayDecision.shouldRedirectToWelcome) {
      console.log('Redirect to welcome page detected during initialization');
      return {
        currentLevel: 1,
        currentWord: sequence[0],
        wordSequence: sequence,
        showInstructions: false,
        showGameCompletion: false,
        showInProgressOverlay: false,
        redirectToWelcome: true,
        attempts: 0,
        gameStatus: 'playing' as GameStatus,
        overlaySnapshot: snapshot
      };
    }
    
    // Start fresh if it's a new day/play again/mode switch or we have no saved state
    if (isNewDay || isPlayAgain || (forceModeChange && !savedState)) {
      try {
        if (savedState && isNewDay) {
          await clearGameState();
          console.log('Cleared previous game state due to new day', {
            isNewDay,
            timestamp: new Date().toISOString()
          });
          
        }
      } catch (error) {
        console.error('Error clearing game state:', error);
      }
      
      // Initialize fresh state
      const initialWord = sequence[0];
      if (!initialWord) {
        throw new Error('No word available for initialization');
      }

      return {
        currentLevel: 1,
        currentWord: initialWord,
        wordSequence: sequence,
        showInstructions: !savedState && !forceModeChange,
        showGameCompletion: false,
        showInProgressOverlay: false,
        redirectToWelcome: false,
        attempts: 0,
        gameStatus: 'playing' as GameStatus
      };
    }

    // Resume existing game
    const currentLevel = savedState?.currentLevel || 1;
    const currentWord = sequence[currentLevel - 1] || sequence[0];
    
    if (!currentWord) {
      throw new Error('No word available for current level');
    }
    
    // Check if game is completed - check both saved state and CompletionStateManager
    const gameCompletion = savedState?.gameStatus === 'won' || savedState?.gameStatus === 'lost' || 
      CompletionStateManager.shouldShowCompletion(currentMode as 'daily' | 'practice');
    
    console.log('Resuming game with:', {
      level: currentLevel,
      word: currentWord,
      status: savedState?.gameStatus,
      showCompletion: gameCompletion,
      showInProgress: overlayDecision.showInProgress,
      redirectToWelcome: overlayDecision.shouldRedirectToWelcome,
      mode: currentMode,
      timestamp: new Date().toISOString()
    });

    return {
      currentLevel,
      currentWord,
      wordSequence: sequence,
      showInstructions: false,
      showGameCompletion: gameCompletion || overlayDecision.showGameCompletion,
      showInProgressOverlay: overlayDecision.showInProgress && !gameCompletion,
      redirectToWelcome: overlayDecision.shouldRedirectToWelcome,
      attempts: savedState?.currentGuessRow || 0,
      gameStatus: savedState?.gameStatus || 'playing',
      guesses: savedState?.guesses || [],
      keyboardStatus: savedState?.keyboardStatus || {},
      guessHistory: savedState?.guessHistory || [],
      previousWord: savedState?.previousWord || null,
      levelAttempts: savedState?.levelAttempts || [],
      overlaySnapshot: snapshot
    };
  } catch (error) {
    console.error('Error during game initialization:', error);
    toast.error("There was an error starting the game. Please refresh the page.");
    // Make sure we include redirectToWelcome in the error case too
    throw error; // Re-throw to be handled by the caller
  }
};
